import React, {FunctionComponent, ReactNode, useState} from 'react';
import {Avatar, Box} from '@mui/material';
import noBizLearnVideoImage from '../../../../assets/images/no-bizlearn-video-image.png';
import Utils from '../../../../core/services/utils/utils';

export type CommandSearchResultIconSize = 'row' | 'card' | 'tutorial';

const ICON_DIMENSIONS: Record<CommandSearchResultIconSize, number> = {
    row: 28,
    card: 32,
    tutorial: 64,
};

interface CommandSearchResultIconProps {
    icon?: ReactNode;
    avatarUrl?: string;
    size?: CommandSearchResultIconSize;
    /** When true, broken / missing images fall back to the BizLearn placeholder. */
    useBizLearnFallback?: boolean;
}

/** Renders a result avatar/icon; tutorials can use a larger thumb with placeholder fallback. */
const CommandSearchResultIcon: FunctionComponent<CommandSearchResultIconProps> = ({
    icon,
    avatarUrl,
    size = 'row',
    useBizLearnFallback = false,
}) => {
    const dimension = ICON_DIMENSIONS[size];
    const [imageFailed, setImageFailed] = useState(false);

    if (useBizLearnFallback) {
        const src = avatarUrl && !imageFailed
            ? (Utils.encodeFileUrl(avatarUrl) as string)
            : noBizLearnVideoImage;
        return (
            <Box
                component="img"
                src={src}
                alt=""
                onError={() => {
                    if (avatarUrl && !imageFailed) {
                        setImageFailed(true);
                    }
                }}
                sx={{
                    width: dimension,
                    height: dimension,
                    flexShrink: 0,
                    borderRadius: 1.5,
                    objectFit: 'cover',
                    bgcolor: 'action.hover',
                }}
            />
        );
    }

    if (avatarUrl) {
        return (
            <Avatar
                src={Utils.encodeFileUrl(avatarUrl) as string}
                alt=""
                sx={{width: dimension, height: dimension, flexShrink: 0}}
            />
        );
    }

    if (!icon) {
        return null;
    }

    return (
        <Box
            sx={{
                width: dimension,
                height: dimension,
                borderRadius: size === 'row' ? 1.25 : 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'action.hover',
                color: 'text.secondary',
                flexShrink: 0,
            }}
        >
            {icon}
        </Box>
    );
};

export default CommandSearchResultIcon;
